import React from "react";
import { isEmpty, values } from "lodash";
import { type ValidationValue } from "../../../utils/validationRules";
import { NativeTypes } from "react-dnd-html5-backend";
import DragAndDropSection from "./DragAndDropSection";

interface DragAndDropSource {
  items: DataTransferItemList;
  files?: File[];
}

export interface DragAndDropSectionHandler {
  canDrop(source: DragAndDropSource): boolean;
  onDrop(source: DragAndDropSource): void;
  renderDragAndDropSection(): React.ReactElement;
  acceptType: string;
}

interface Props {
  onFileDropped: (file: File) => void;
  isValidFileType: (value: ValidationValue) => boolean;
  isValidFileExtension: (file: File) => boolean;
}

export const useDragAndDropSection = ({
  onFileDropped,
  isValidFileType,
  isValidFileExtension,
}: Props): DragAndDropSectionHandler => {
  const hasMultipleFilesRef = React.useRef(false);

  const canDrop = React.useCallback(
    (source: DragAndDropSource) => {
      const { items } = source;
      hasMultipleFilesRef.current = items.length > 1;

      const csvExists = isValidFileType(items);
      const emptyExists = values(source.items).some((item) => isEmpty(item.type));
      return !items || items.length === 0 || csvExists || emptyExists;
    },
    [isValidFileType],
  );

  const onDrop = React.useCallback(
    (source: DragAndDropSource) => {
      const { files } = source;
      if (!files || files.length !== 1) return;
      const file = files[0];
      if (!isValidFileExtension(file)) return;
      onFileDropped(file);
    },
    [isValidFileExtension, onFileDropped],
  );

  const renderDragAndDropSection = React.useCallback(
    () => <DragAndDropSection hasMultipleFiles={hasMultipleFilesRef.current} />,
    [],
  );

  return {
    acceptType: NativeTypes.FILE,
    canDrop,
    onDrop,
    renderDragAndDropSection,
  };
};
